// Body-start classifier. A one-piece txt often carries planning front matter
// before the actual screenplay: 项目大纲 / 角色卡 / 宏观结构 / 一卡集纲. Those
// pages normalize into stray scenes/episodes (a scene before any episode crashes
// assemble; 集纲 summaries inflate the episode count). This task judges a numbered
// window of source lines and, if the real screenplay body begins within it,
// reports that line number. The pipeline scans windows forward until one hits.
//
// Text (not tool) mode: it runs on the cheap deepseek-flash profile, which does
// not support function calling. The model emits a single line `起点: <行号>` or
// `起点: 无`, parsed deterministically here.

import type { LlmTaskExecutor } from "../../types.js";

export const BODY_START_SYSTEM =
  "你是短剧剧本的「正文起点定位器」。一份文件常常在真正的剧本正文之前，压着一大段**非正文**的筹备材料。你要判断：**真正的剧本正文**是不是在我给你的这段带行号的文本里开始，如果是，报告它的起始行号。" +
  "\n\n【正文长什么样】逐场的剧本：以「第N集」为集头，下面紧跟「场X-Y  日/内 地点」这种带场次编号的场景头，再往下是「主要人物：/人物：」和一条条以 △ 开头的动作、以及带说话人的台词。这才是正文。" +
  "\n\n【前言长什么样，绝不算正文】：" +
  "\n- 项目基本信息 / 策划大纲 / 题材标签 / 一句话故事 / 核心看点；" +
  "\n- 角色卡：一段段「人物身份 / 外貌和服饰特点 / 性格标签 / 台词语言风格 / 人物小传」的介绍；" +
  "\n- 宏观结构大纲：「阶段一 / 阶段二……」这种整段叙事概述（读起来像剧情简介，没有场次编号、没有逐条台词）；" +
  "\n- 一卡集纲：也会写「第N集：小标题」，但下面只是**一句话到一段话的剧情摘要**，没有「场X-Y」场次、没有 △ 动作、没有逐句台词。这是最容易误判的陷阱——它有「第N集」但**不是正文**。" +
  "\n\n【判定规则】只有当你在窗口里看到**带场次编号（场X-Y）或明显逐条动作/台词的剧集**时，才算正文开始。取正文的第一行：通常是正文那个「第N集」集头所在行（若没有集头就是第一个「场X-Y」行）。如果文件里有「剧本正文」「分集剧本」这类分隔标题，正文就从它之后的第一个集头/场次开始。" +
  "\n\n【边界与保守】" +
  "\n- 只报告**本窗口内**能确认的行号；如果这段还全是前言、或正文还没出现，就报告『无』，我会把下一段给你。" +
  "\n- 宁可稍微靠前也不要跳过第 1 集：拿不准该取集头还是场次行时，取更靠前的那一行。" +
  "\n- 行号必须原样照抄我给你的行首数字。" +
  "\n\n【输出格式】只输出一行，不要任何解释：\n- 找到正文起点：`起点: <行号>`（例如 `起点: 140`）\n- 本窗口全是前言 / 还没到正文：`起点: 无`";

export interface BodyStartResult {
  found: boolean;
  line: number | null;
}

/** Parse the model's single-line reply. Lenient: finds `起点: <n>` anywhere. */
export function parseBodyStart(text: string): BodyStartResult {
  const m = text.match(/起点\s*[:：]\s*(\d+)/);
  if (m) return { found: true, line: Math.trunc(Number(m[1])) };
  return { found: false, line: null };
}

/** One numbered window → does the screenplay body start here, and at which line. */
export async function runBodyStart(
  executor: LlmTaskExecutor,
  numberedWindow: string,
  signal?: AbortSignal,
): Promise<BodyStartResult> {
  const res = await executor.generateText("ingest.body-start", {
    system: BODY_START_SYSTEM,
    user:
      "下面是源文件的一段，每行以「行号<TAB>内容」给出。判断真正的剧本正文是否在这段里开始，按格式只回一行。\n\n" +
      numberedWindow,
    ...(signal ? { signal } : {}),
  });
  return parseBodyStart(res.text);
}
